'use client';
import { memo, useCallback, useMemo } from 'react';

import { type Board } from '@air/api/types';
import { useAirModal } from '@air/provider-modal';

import { useLibraryPermissions } from '~/components/LibraryBeta/hooks/useLibraryPermissions';
import { CreateBoardModal } from '~/components/Modals/CreateBoardModal';
import { CreateDropdownButton } from '~/components/Shared/HeaderBar/CreateDropdownButton';
import { useWorkspaceRightPanelOpen } from '~/components/v2/store/features/workspace/useWorkspaceRightPanelOpen';
import { useDraftCanvas } from '~/hooks/canvases/useDraftCanvas';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { getCreateMenuOptions } from '~/utils/dropdownMenuOptions/getCreateMenuOptions';
import { canCreateBoard } from '~/utils/permissions/boardPermissions';

export type LibraryTopBarAddButtonProps = {
  library: Board['library'];
};

export const LibraryTopBarAddButton = memo(({ library }: LibraryTopBarAddButtonProps) => {
  const { libraryPermissions } = useLibraryPermissions({ libraryId: library?.id });
  const [setShowCreatePrivateBoardModal] = useAirModal(CreateBoardModal);
  const { generativeCanvasEnabled } = useTemporaryFeatureFlags();
  const isRightPanelOpen = useWorkspaceRightPanelOpen();
  const { openDraftCanvas } = useDraftCanvas();

  const handleCreateBoard = useMemo(
    () =>
      canCreateBoard(libraryPermissions)
        ? () => {
            setShowCreatePrivateBoardModal({
              initialLibrary: library,
              trackLocation: 'library',
            });
          }
        : undefined,
    [library, libraryPermissions, setShowCreatePrivateBoardModal],
  );

  const handleCreateCanvas = useCallback(() => {
    openDraftCanvas({
      analyticsContext: { source: 'library', library_id: library?.id },
    });
  }, [library?.id, openDraftCanvas]);

  const options = useMemo(
    () =>
      getCreateMenuOptions({
        onCreateBoard: handleCreateBoard,
        onCreateCanvas: generativeCanvasEnabled ? handleCreateCanvas : undefined,
      }),
    [generativeCanvasEnabled, handleCreateBoard, handleCreateCanvas],
  );

  if (!isRightPanelOpen) {
    return <CreateDropdownButton options={options} />;
  }

  return null;
});

LibraryTopBarAddButton.displayName = 'LibraryTopBarAddButton';
